import { parametrosDoObjeto } from './parametros'
import { alturaDoObjetoTerrenoEm, ehTerreno } from './terreno'
import { ALCAPAO_TORRE_M, pisosDaTorre } from './torre'
import type { CenaTabletop, ObjetoTabletop } from './tipos'

/**
 * As superfícies em que uma pessoa pode estar de pé ou cair, em vários níveis:
 * o terreno (um chão só por ponto, sólido para baixo) e os pisos das torres
 * (patamares de grade e a plataforma do topo, finos, com o alçapão aberto).
 *
 * O terreno sozinho é um mapa de alturas; dentro de uma torre há vários pisos
 * no mesmo (x, z), e o que importa é o que fica abaixo de uma altura dada.
 * TypeScript puro, com testes.
 */

export type TipoSuperficie = 'terreno' | 'piso'

export interface SuperficieNoPonto {
  readonly altura: number
  readonly tipo: TipoSuperficie
  /** Para os avisos e o impacto: a peça de terreno, o chão ou o patamar da torre. */
  readonly nome: string
}

/** O terreno no ponto e a peça que o define (a mais alta que cobre o ponto). */
export function terrenoNoPonto(cena: CenaTabletop, x: number, z: number): SuperficieNoPonto {
  let melhor: SuperficieNoPonto = { altura: 0, tipo: 'terreno', nome: 'o chão' }
  for (const objeto of cena.objetos) {
    if (!ehTerreno(objeto.tipo) || objeto.oculto) continue
    const h = alturaDoObjetoTerrenoEm(objeto, x, z)
    if (h !== null && h > melhor.altura) melhor = { altura: h, tipo: 'terreno', nome: objeto.nome }
  }
  return melhor
}

/** Leva (x, z) do mundo para o referencial da torre (só o giro em Y e a escala). */
export function paraLocal(objeto: ObjetoTabletop, x: number, z: number): { lx: number; lz: number } {
  const { posicao, rotacao, escala } = objeto.transformacao
  const dx = x - posicao[0]
  const dz = z - posicao[2]
  const cos = Math.cos(rotacao[1])
  const sen = Math.sin(rotacao[1])
  return { lx: (dx * cos - dz * sen) / escala[0], lz: (dx * sen + dz * cos) / escala[2] }
}

/**
 * Os pisos de uma torre que passam pelo ponto (x, z): cada patamar e a
 * plataforma do topo, menos o quadrado do alçapão de cada um (o canto alterna
 * de um piso para o outro), por onde se cai para o piso de baixo.
 */
export function pisosDaTorreEm(torre: ObjetoTabletop, x: number, z: number): readonly SuperficieNoPonto[] {
  if (torre.tipo !== 'torre' || torre.oculto) return []
  const { alturaM, larguraM, espacoPatamarM } = parametrosDoObjeto(torre)
  const { lx, lz } = paraLocal(torre, x, z)
  const meio = larguraM! / 2
  if (Math.abs(lx) > meio || Math.abs(lz) > meio) return []
  const alcapao = Math.min(ALCAPAO_TORRE_M, larguraM! * 0.45)
  const base = torre.transformacao.posicao[1]
  const escalaY = torre.transformacao.escala[1]
  return pisosDaTorre(alturaM!, espacoPatamarM!)
    // cada piso tem o alçapão num canto (alternando de um piso para o outro): ali não há chão
    .filter(({ alcapao: canto }) => !canto || !(canto[0] * lx > meio - alcapao && canto[1] * lz > meio - alcapao))
    .map(({ altura: h }) => ({
      altura: base + h * escalaY,
      tipo: 'piso' as const,
      nome: h === alturaM ? `o topo da ${torre.nome}` : `o patamar de ${String(h).replace('.', ',')} m da ${torre.nome}`,
    }))
}

/** Todas as superfícies no ponto, de cima para baixo: o terreno e os pisos das torres. */
export function superficiesEm(cena: CenaTabletop, x: number, z: number): readonly SuperficieNoPonto[] {
  const todas = [terrenoNoPonto(cena, x, z), ...cena.objetos.flatMap((o) => pisosDaTorreEm(o, x, z))]
  return todas.sort((a, b) => b.altura - a.altura)
}

/**
 * A superfície mais alta no ponto que não passa de `yLimite`: onde algo que
 * está em `yLimite` (ou desce dali) se apoia. O terreno sempre existe, então
 * sempre há uma resposta.
 */
export function superficieSob(cena: CenaTabletop, x: number, z: number, yLimite: number): SuperficieNoPonto {
  const terreno = terrenoNoPonto(cena, x, z)
  let melhor: SuperficieNoPonto | null = null
  for (const s of superficiesEm(cena, x, z)) {
    if (s.altura <= yLimite + 1e-9 && (!melhor || s.altura > melhor.altura)) melhor = s
  }
  // abaixo do terreno não há nada a não ser o próprio terreno
  return melhor ?? terreno
}

/** O apoio acabou quando a superfície cai mais que isto abaixo dos pés. */
export const DESNIVEL_SEM_APOIO_M = 0.15

/**
 * Quem está de pé em `yPes` no ponto (x, z): apoiado (há uma superfície no nível
 * dos pés) ou no vazio, e então a superfície onde cairia, a primeira abaixo.
 */
export function apoioNoPonto(
  cena: CenaTabletop,
  x: number,
  z: number,
  yPes: number,
): { readonly apoiado: boolean; readonly superficie: SuperficieNoPonto } {
  const superficie = superficieSob(cena, x, z, yPes + DESNIVEL_SEM_APOIO_M)
  return { apoiado: superficie.altura >= yPes - DESNIVEL_SEM_APOIO_M, superficie }
}
